import { useState } from 'react';
import { Upload, Button, Space, Image, Tooltip, theme, useApp } from '../ui';
import { Upload as UploadIcon, Trash2, FileText, Video, Image as ImageIcon, File } from 'lucide-react';
import type { UploadProps } from '../ui';
import { api } from '../api/client';
import { PALETTE } from '../lib/palette';
import { fileLabel } from '../lib/fileName';

export type UploadCategory =
  | 'product-image' | 'product-video' | 'product-brochure'
  | 'retailer-photo' | 'farmer-photo' | 'profile-photo'
  | 'receipt' | 'evidence' | 'pod' | 'attachment' | 'invoice' | 'event-photo' | 'banner' | 'payment-qr'
  | 'company-seal' | 'company-signature';

const ACCEPT: Record<UploadCategory, string> = {
  'product-image':    'image/png,image/jpeg,image/webp,image/gif,image/svg+xml',
  'product-video':    'video/mp4,video/quicktime,video/webm',
  'product-brochure': 'application/pdf',
  'retailer-photo':   'image/*',
  'farmer-photo':     'image/*',
  'profile-photo':    'image/*',
  'receipt':          'image/*,application/pdf',
  'evidence':         'image/*,application/pdf,video/*',
  'pod':              'image/*,application/pdf',
  'attachment':       '*/*',
  'invoice':          'application/pdf',
  'event-photo':      'image/*',
  'banner':           'image/png,image/jpeg,image/webp,image/gif,image/svg+xml',
  'payment-qr':       'image/png,image/jpeg,image/webp',
  'company-seal':       'image/png,image/jpeg,image/webp',
  'company-signature':  'image/png,image/jpeg,image/webp',
};

const MAX_MB: Record<UploadCategory, number> = {
  'product-image': 5, 'product-video': 50, 'product-brochure': 20,
  'retailer-photo': 5, 'farmer-photo': 5, 'profile-photo': 5,
  'receipt': 10, 'evidence': 25, 'pod': 10, 'attachment': 25, 'invoice': 20, 'event-photo': 5,
  'banner': 8, 'payment-qr': 2, 'company-seal': 2, 'company-signature': 2,
};

interface Props {
  /** Current URL — comes from / goes to the surrounding Form.Item via value/onChange */
  value?: string;
  onChange?: (url: string) => void;
  category: UploadCategory;
  /** Optional label hint shown on the button */
  buttonLabel?: string;
  /** Show inline thumbnail (only meaningful for images) */
  preview?: boolean;
  size?: 'small' | 'middle' | 'large';
  disabled?: boolean;
}

function fileIcon(category: UploadCategory, url?: string): JSX.Element {
  if (!url) return <File size={16} />;
  const lower = url.toLowerCase();
  if (category.includes('image') || /\.(png|jpe?g|webp|gif|svg)(\?|$)/.test(lower)) return <ImageIcon size={16} />;
  if (category.includes('video') || /\.(mp4|mov|webm)(\?|$)/.test(lower)) return <Video size={16} />;
  if (category.includes('brochure') || /\.pdf(\?|$)/.test(lower)) return <FileText size={16} />;
  return <File size={16} />;
}

/**
 * Upload ONE file to a category and hand back its URL.
 *
 * Presents exactly like {@link MultiFileUploadField}: an upload button with a short hint, then a
 * 96px tile — image preview, or a file icon plus filename that opens in a new tab — with a small
 * round remove button on its corner. The two used to look nothing alike (this one had separate
 * Open and Delete buttons plus a raw URL chip), which was obvious the moment a form put a
 * single-file field beside a multi-file one, as the farmer form does with Photo and KYC.
 */
export function FileUploadField({
  value, onChange, category, buttonLabel, preview = true, size = 'middle', disabled, compact = false,
}: Props & {
  /** Just the upload button — no file-count hint and no file card. For a row that already shows its own
   *  preview of the file, where a second card (with a remove button the row cannot honour) is noise. */
  compact?: boolean;
}) {
  const { token } = theme.useToken();
  const { message } = useApp();
  const [busy, setBusy] = useState(false);
  const btnSize = size === 'small' ? 'sm' : size === 'large' ? 'lg' : 'md';

  const uploadProps: UploadProps = {
    accept: ACCEPT[category],
    showUploadList: false,
    multiple: false,
    disabled: disabled || busy,
    beforeUpload: async (file) => {
      const maxBytes = MAX_MB[category] * 1024 * 1024;
      if (file.size > maxBytes) {
        message.error(`File too large — max ${MAX_MB[category]} MB`);
        return Upload.LIST_IGNORE;
      }
      setBusy(true);
      const form = new FormData();
      form.append('file', file);
      try {
        const r = await api.post(`/uploads/${category}`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const data = r.data?.data ?? r.data;
        const url = data?.url;
        if (!url) throw new Error('Server did not return a URL');
        onChange?.(url);
        message.success(`Uploaded ${file.name}`);
      } catch (e: any) {
        message.error(e.response?.data?.error?.message ?? e.message ?? 'Upload failed');
      } finally {
        setBusy(false);
      }
      return Upload.LIST_IGNORE;
    },
  };

  const isImg = (u: string) => /\.(png|jpe?g|webp|gif|svg)(\?|$)/i.test(u);
  const isPdf = (u: string) => /\.pdf(\?|$)/i.test(u);
  const isVideo = (u: string) => /\.(mp4|mov|m4v|webm|ogv|ogg)(\?|$)/i.test(u);
  // The name it was uploaded under — the stored name carries a uniqueness id nobody needs to read,
  // and files stored before uploads kept their name are called what they are (see lib/fileName).
  const fileName = value ? fileLabel(value) : '';
  // Show the picture only when the caller wants a preview AND the file is one.
  const asImage = !!value && preview && isImg(value);

  return (
    <Space direction="vertical" size={10} style={{ width: '100%' }}>
      <Space wrap>
        <Upload {...uploadProps}>
          <Button size={btnSize} variant="default" icon={<UploadIcon size={16} />} loading={busy} disabled={disabled}>
            {buttonLabel ?? (value ? 'Replace file' : `Upload ${category.split('-').join(' ')}`)}
          </Button>
        </Upload>
        {!compact && <span style={{ color: token.colorTextTertiary, fontSize: 12 }}>
          {value ? '1 file' : 'No file'} · max {MAX_MB[category]} MB
        </span>}
      </Space>

      {value && !compact && (
        <div style={{ position: 'relative', border: `1px solid ${token.colorBorderSecondary}`, borderRadius: 8, padding: 4, width: 96 }}>
          {asImage ? (
            <Image src={value} width={86} height={70} style={{ objectFit: 'cover', borderRadius: 6 }} preview={{ src: value }} />
          ) : (
            <a
              href={value}
              target="_blank"
              rel="noopener"
              title={fileName}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 70, gap: 4, color: token.colorText }}
            >
              {isPdf(value) ? <FileText size={24} color={PALETTE.danger[600]} />
                : isVideo(value) ? <Video size={24} color={token.colorPrimary} />
                : isImg(value) ? <ImageIcon size={24} />
                : <File size={24} />}
              <span style={{ fontSize: 10, maxWidth: 84, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fileName}</span>
            </a>
          )}
          <Tooltip title="Remove">
            <Button
              size="sm" shape="circle" variant="danger" aria-label="Remove file"
              icon={<Trash2 size={16} />} onClick={() => onChange?.('')} disabled={disabled}
              style={{ position: 'absolute', top: -8, right: -8 }}
            />
          </Tooltip>
        </div>
      )}
    </Space>
  );
}
